import { useClassNames } from "@/utils/classNames";
import { Icon } from "@/components/atoms/Icon";

import styles from "./SingleSelect.module.scss";
import { SingleSelectProps } from "./types";

/**
 * SingleSelect component allows a user to select a single item in a list of options.
 *
 * @param active - If the single select is activated
 * @param icon - Optional icon to display before the text
 * @param className - Additional CSS class for styling
 * @param children - Content to display inside the single select
 * @param attr - Additional HTML attributes to pass to the rendered element
 *
 * @returns the rendered SingleSelect component
 *
 * @example
 * ```tsx
 * <SingleSelect
 *    active={true}
 *    icon={StreamingIcons.Check}
 * >
 *    Label
 * </SingleSelect>
 * ```
 */
const SingleSelect = ({
  active = false,
  icon,
  className,
  textClassName,
  children,
  ...attr
}: SingleSelectProps) => {
  const cn = useClassNames(
    styles,
    styles["single-select"],
    active ? styles["active"] : styles["inactive"],
    icon && styles["icon"],
    className,
  );

  const textCn = useClassNames(styles, styles["text"], textClassName);

  return (
    <div className={cn} {...attr}>
      <span className={textCn}>{children}</span>
      {icon && <Icon icon={icon} className={styles["icon"]} />}
    </div>
  );
};

export { SingleSelect };
